import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

interface EmailChipInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  "data-testid"?: string;
}

const serialize = (emails: string[]): string => JSON.stringify(emails);

const splitTokens = (raw: string): string[] =>
  raw
    .split(/[,;\n]/)
    .map((part) => part.trim())
    .filter(Boolean);

const uniqueEmails = (emails: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const email of emails) {
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(email);
  }
  return out;
};

const EmailChipInput: React.FC<EmailChipInputProps> = ({
  value,
  onChange,
  disabled,
  placeholder = "Add email and press Enter",
  id,
  "data-testid": testId,
}) => {
  const [chips, setChips] = useState(value);
  const [draft, setDraft] = useState("");
  const lastEmitted = useRef(serialize(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (serialize(value) === lastEmitted.current) return;
    setChips(value);
    lastEmitted.current = serialize(value);
  }, [value]);

  const emit = (next: string[]) => {
    setChips(next);
    lastEmitted.current = serialize(next);
    onChange(next);
  };

  const commit = (raw: string) => {
    const added = splitTokens(raw);
    if (!added.length) {
      setDraft("");
      return;
    }
    emit(uniqueEmails([...chips, ...added]));
    setDraft("");
  };

  const removeAt = (index: number) => {
    emit(chips.filter((_, itemIndex) => itemIndex !== index));
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit(draft);
      return;
    }
    if (event.key !== "Backspace" && event.key !== "Delete") return;
    const target = event.currentTarget;
    const caretAtStart = (target.selectionStart ?? 0) === 0 && (target.selectionEnd ?? 0) === 0;
    if (!caretAtStart || !chips.length) return;
    event.preventDefault();
    emit(chips.slice(0, -1));
  };

  return (
    <div
      className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5"
      data-testid={testId}
      onClick={() => inputRef.current?.focus()}
    >
      {chips.map((email, index) => (
        <Badge
          key={`${email.toLowerCase()}-${index}`}
          variant="secondary"
          className="gap-1 font-normal"
          onClick={(event) => event.stopPropagation()}
        >
          {email}
          <button
            type="button"
            aria-label={`Remove ${email}`}
            disabled={disabled}
            className="rounded-full p-0.5 hover:bg-muted"
            onClick={() => removeAt(index)}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <Input
        id={id}
        ref={inputRef}
        value={draft}
        disabled={disabled}
        placeholder={chips.length ? "" : placeholder}
        onChange={(event) => {
          const next = event.target.value;
          if (next.includes(",") || next.includes(";")) {
            commit(next);
            return;
          }
          setDraft(next);
        }}
        onKeyDown={onInputKeyDown}
        onBlur={() => commit(draft)}
        className="h-7 min-w-[12rem] flex-1 border-0 px-1 shadow-none focus-visible:ring-0"
      />
    </div>
  );
};

export default EmailChipInput;
